/**
 * Salesforce Project API Service
 *
 * This service handles all communication with Salesforce REST APIs
 * for creating and managing Projects and Project Objectives.
 */

import axios from "axios";
import { getEnv } from "../config/getEnv";
import { storage } from "../auth/storage";

// Configuration from environment variables
const SALESFORCE_INSTANCE_URL =
  getEnv("REACT_APP_SF_BASE_URL") ||
  getEnv("REACT_APP_SALESFORCE_INSTANCE_URL") ||
  "https://appen-mercury--staging.sandbox.my.salesforce.com";

const getAccessToken = () => {
  if (typeof window !== "undefined") {
    try {
      const t = storage.readSfAuthSession()?.tokenSet?.access_token;
      if (t) return t;
    } catch {
      /* ignore */
    }
  }
  return "";
};

// Log configuration for debugging
const token = getAccessToken();
console.log("🔧 Salesforce Project API Configuration:", {
  baseURL: SALESFORCE_INSTANCE_URL,
  hasToken: !!token,
  tokenPreview: token
    ? `${token.substring(0, 10)}...`
    : "MISSING (sign in with Salesforce)",
});

// Create axios instance with default config
const salesforceApi = axios.create({
  baseURL: `${SALESFORCE_INSTANCE_URL}/services/apexrest`,
  timeout: 30000, // 30 seconds
  withCredentials: getEnv("REACT_APP_SF_WITH_CREDENTIALS") === "true",
  // Accept all 2xx status codes as successful, including 201 (Created) for POST requests
  validateStatus: (status) => status >= 200 && status < 300,
});

salesforceApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Content-Type"] = "application/json";
    return config;
  },
  (error) => Promise.reject(error),
);

// Add response interceptor for error handling
salesforceApi.interceptors.response.use(
  (response) => {
    // Explicitly handle 201 (Created) as success for create operations
    // All 2xx responses (200, 201, etc.) are successful
    if (response.status >= 200 && response.status < 300) {
      console.log(
        `✅ Salesforce API Success: ${response.config?.method?.toUpperCase()} ${response.config?.url} - Status ${response.status}`,
      );
    }
    return response;
  },
  (error) => {
    console.error(
      "Salesforce API Error:",
      error.response?.data || error.message,
    );
    if (error.response?.status === 401) {
      console.error(
        "⚠️ Unauthorized: Token may be expired or invalid. Re-authenticate Salesforce.",
      );
    }
    return Promise.reject(error);
  },
);

/**
 * Map UI metadata fields to Salesforce Project fields
 */
const mapProjectMetadataToSalesforce = (metadata) => {
  console.log(
    "🔍 mapProjectMetadataToSalesforce - category value:",
    metadata.category,
  );

  const programName = String(metadata.programName || "").trim();

  return {
    // Basic Information
    Name: metadata.name,
    Short_Project_Name__c: metadata.shortName,
    Contributor_Project_Name__c: metadata.contributorProjectName,
    Project_Short_Description__c: metadata.shortDescription,
    Project_Long_Description__c: metadata.longDescription,
    Workday_Project_ID__c: metadata.workdayProjectId ?? "",
    ...(programName && programName !== "-- None --"
      ? { Program_Name__c: programName }
      : {}),
    // Job_Category__c - DISABLED: Do not send this field to avoid picklist errors
    ...(metadata.category ? { Job_Category__c: metadata.category } : {}),
    // Note: Appen_Partner__c is a lookup field that requires a valid Salesforce ID, not text
    // Only include if it's a valid ID (starts with 'a' or '0' and is 15 or 18 chars)
    ...(metadata.appenPartner && { Appen_Partner__c: metadata.appenPartner }),
    // Note: Account__c is a lookup field that requires a valid Salesforce ID
    ...(metadata.account &&
    (metadata.account.length === 15 || metadata.account.length === 18)
      ? { Account__c: metadata.account }
      : {}),

    // Dates
    Hire_Start_Date__c: metadata.hireStartDate,
    Predicted_Close_Date__c: metadata.predictedCloseDate,

    // Status and Type
    Project_Status__c: metadata.projectStatus,
    Project_Type__c: metadata.projectType,
    Project_Priority__c: Number(metadata.projectPriority),
    Project_Payment_Method__c: metadata.projectPaymentMethod,

    // People - These are lookup fields that expect Salesforce User IDs, not emails/names
    // Now we use the *Id fields which are populated by UserLookupField component
    // Validate that IDs start with '005' (User ID prefix) and are 15 or 18 characters before sending
    // TEMPORARILY DISABLED: Skip all user lookup fields to test if project creation works
    // ...(metadata.projectManagerId &&
    // String(metadata.projectManagerId).trim().startsWith("003") &&
    // (metadata.projectManagerId.trim().length === 15 ||
    //   metadata.projectManagerId.trim().length === 18)
    //   ? { Project_Manager__c: metadata.projectManagerId.trim() }
    //   : {}),
    // ...(metadata.qualityLeadId && String(metadata.qualityLeadId).trim().startsWith('005') && (metadata.qualityLeadId.trim().length === 15 || metadata.qualityLeadId.trim().length === 18) ? { Quality_Lead__c: metadata.qualityLeadId.trim() } : {}),
    // ...(metadata.productivityLeadId && String(metadata.productivityLeadId).trim().startsWith('005') && (metadata.productivityLeadId.trim().length === 15 || metadata.productivityLeadId.trim().length === 18) ? { Productivity_Lead__c: metadata.productivityLeadId.trim() } : {}),
    // ...(metadata.reportingLeadId && String(metadata.reportingLeadId).trim().startsWith('005') && (metadata.reportingLeadId.trim().length === 15 || metadata.reportingLeadId.trim().length === 18) ? { Reporting_Lead__c: metadata.reportingLeadId.trim() } : {}),
    // ...(metadata.invoicingLeadId && String(metadata.invoicingLeadId).trim().startsWith('005') && (metadata.invoicingLeadId.trim().length === 15 || metadata.invoicingLeadId.trim().length === 18) ? { Invoicing_Lead__c: metadata.invoicingLeadId.trim() } : {}),
    // ...(metadata.projectSupportLeadId && String(metadata.projectSupportLeadId).trim().startsWith('005') && (metadata.projectSupportLeadId.trim().length === 15 || metadata.projectSupportLeadId.trim().length === 18) ? { Project_Support_Lead__c: metadata.projectSupportLeadId.trim() } : {}),
    // ...(metadata.recruitmentLeadId && String(metadata.recruitmentLeadId).trim().startsWith('005') && (metadata.recruitmentLeadId.trim().length === 15 || metadata.recruitmentLeadId.trim().length === 18) ? { Recruitment_Lead__c: metadata.recruitmentLeadId.trim() } : {}),
    // ...(metadata.qualificationLeadId && String(metadata.qualificationLeadId).trim().startsWith('005') && (metadata.qualificationLeadId.trim().length === 15 || metadata.qualificationLeadId.trim().length === 18) ? { Qualification_Lead__c: metadata.qualificationLeadId.trim() } : {}),
    // ...(metadata.onboardingLeadId && String(metadata.onboardingLeadId).trim().startsWith('005') && (metadata.onboardingLeadId.trim().length === 15 || metadata.onboardingLeadId.trim().length === 18) ? { Onboarding_Lead__c: metadata.onboardingLeadId.trim() } : {}),

    // Requirements for Contributor Active Status
    Payment_Setup_Required__c: metadata.paymentSetupRequired || true,
    Manual_Activation_Required__c: metadata.manualActivationRequired || false,
    Client_Tool_Account_Required__c:
      metadata.clientToolAccountRequired || false,
    Cases_DC_Support_Team__c: metadata.casesDcSupportTeam || false,

    // Admin
    Mercury_Project_Type__c: metadata.mercuryProjectType,
    Client__c: metadata.client,

    // Payment Configurations
    Require_PM_Approval_for_Productivity__c:
      metadata.requirePMApproval || false,
    Release_System_Tracked_Data__c: metadata.releaseSystemTrackedData,

    // Activation
    Activate_Comms_Invited__c: metadata.activateCommsInvited || false,
    Activate_Comms_Applied__c: metadata.activateCommsApplied || false,
    Activate_Comms_Onboarding__c: metadata.activateCommsOnboarding || false,
    Activate_Comms_Failed__c: metadata.activateCommsFailed || false,

    // Lever Requisition
    Requisition_Action__c: metadata.requisitionAction,
    Lever_Requisition_ID__c: metadata.leverRequisitionId,
    Lever_Requisition_Create_Date__c: metadata.leverRequisitionCreateDate,
  };
};

/**
 * Map UI objective data to Salesforce Project Objective fields
 */
const mapObjectiveToSalesforce = (objective, projectId) => {
  console.log(
    "📅 Mapping objective - dateStart:",
    objective.dateStart,
    "dateEnd:",
    objective.dateEnd,
  );
  console.log("📅 Full objective object:", objective);
  const currentlyResidingUsState = (states) => {
    if (!Array.isArray(states) || states.length === 0) return "";
    return states.join(";");
  };

  const base = {
    // Required fields
    Name: objective.objectiveName || objective.name,
    Project__c: projectId, // Link to parent project

    // Information
    Contributor_Facing_Project_Name__c: objective.contributorFacingName,
    Append_to_Objective_Name__c: objective.appendToObjectiveName,
    // Dates included only if provided to avoid Apex Date.valueOf(null)
    ...(objective.dateStart ? { Date_Start__c: objective.dateStart } : {}),
    ...(objective.dateEnd ? { Date_End__c: objective.dateEnd } : {}),
    Selection_Criteria__c: objective.selectionCriteria,
    Project_Objective_Description__c: objective.projectObjectiveDescription,
    Target_Contributors__c: objective.targetContributors
      ? Number(objective.targetContributors)
      : null,

    // Requirements
    Country__c: objective.country,
    Work_Type__c: objective.workType,
    Language__c: objective.language,
    Dialect_Country__c: objective.dialectCountry || objective.dialect,
    Language_Skill_Level__c: objective.languageSkillLevel,
    Fluency_Type__c: objective.fluencyType,
    Degree_Requirement__c: objective.degreeRequirement,
    Additional_Manual_Match_Required__c:
      objective.additionalManualMatchRequired || false,
    Cap_Daily_Hours_Submitted__c: objective.capDailyHoursSubmitted
      ? Number(objective.capDailyHoursSubmitted)
      : null,
    Include_in_Country_List__c: objective.includeInCountryList || false,
    List_of_Countries__c:
      Array.isArray(objective.listOfCountries) &&
      objective.listOfCountries.length > 0
        ? objective.listOfCountries.join(";")
        : "",
    Exclude_States__c: Boolean(objective.excludeStates),
    Currently_Residing_US_State__c: currentlyResidingUsState(
      objective.currentlyResidingUsState,
    ),
    // Productivity & Quality
    Productivity_Target_Type__c: objective.productivityTargetType,
    Unit_Pay_Rate__c: objective.unitPayRate
      ? Number(objective.unitPayRate)
      : null,
    Target_Production_Units__c: objective.targetProductionUnits
      ? Number(objective.targetProductionUnits)
      : null,
    Target_Production_Hours__c: objective.targetProductionHours
      ? Number(objective.targetProductionHours)
      : null,
    Unit_per_Hour__c: objective.unitPerHour
      ? Number(objective.unitPerHour)
      : null,
    Production_Payment_Threshold__c: objective.productionPaymentThreshold
      ? Number(objective.productionPaymentThreshold)
      : null,
    Quality_Payment_Threshold__c: objective.qualityPaymentThreshold
      ? Number(objective.qualityPaymentThreshold)
      : null,

    // Funnel Configuration (Decimals 0/1 expected by Apex). Only include when provided
    ...(objective.funnelAPercentage !== undefined &&
    objective.funnelAPercentage !== null &&
    String(objective.funnelAPercentage) !== ""
      ? { to_use_Funnel_A__c: Number(objective.funnelAPercentage) > 0 ? 1 : 0 }
      : {}),
    ...(objective.funnelBPercentage !== undefined &&
    objective.funnelBPercentage !== null &&
    String(objective.funnelBPercentage) !== ""
      ? { to_use_Funnel_B__c: Number(objective.funnelBPercentage) > 0 ? 1 : 0 }
      : {}),
    ...(objective.funnelCPercentage !== undefined &&
    objective.funnelCPercentage !== null &&
    String(objective.funnelCPercentage) !== ""
      ? { to_use_Funnel_C__c: Number(objective.funnelCPercentage) > 0 ? 1 : 0 }
      : {}),
    ...(objective.funnelDPercentage !== undefined &&
    objective.funnelDPercentage !== null &&
    String(objective.funnelDPercentage) !== ""
      ? { to_use_Funnel_D__c: Number(objective.funnelDPercentage) > 0 ? 1 : 0 }
      : {}),
    ...(objective.funnelEPercentage !== undefined &&
    objective.funnelEPercentage !== null &&
    String(objective.funnelEPercentage) !== ""
      ? { to_use_Funnel_E__c: Number(objective.funnelEPercentage) > 0 ? 1 : 0 }
      : {}),

    // Rates & Payment
    Minimum_Rate__c: objective.minimumRate
      ? Number(objective.minimumRate)
      : null,
    Maximum_Rate__c: objective.maximumRate
      ? Number(objective.maximumRate)
      : null,
    Project_Rate__c: objective.projectRate
      ? Number(objective.projectRate)
      : null,
    Project_Objective_Pay_Rate__c: objective.projectObjectivePayRate
      ? Number(objective.projectObjectivePayRate)
      : null,
    Flat_Pay_Rate__c: objective.flatPayRate
      ? Number(objective.flatPayRate)
      : null,
    Project_Incentive_Rate__c: objective.projectIncentiveRate
      ? Number(objective.projectIncentiveRate)
      : null,

    // Action Rules & Flagging
    Days_Between_Reminder_Emails__c: objective.daysBetweenReminderEmails
      ? Number(objective.daysBetweenReminderEmails)
      : null,
    Email_Content_Complete__c: objective.emailContentComplete || false,

    // Admin
    Status__c: objective.status || "Draft",
    Mercury_Project_Objective_Type__c: objective.mercuryProjectObjectiveType,
    Override_DNH__c: objective.overrideDNH || false,
    Display_Lever_Post_Error_Message__c: objective.displayLeverPostErrorMessage,
    Create_Invites__c: objective.createInvites || false,
    Country_Locale__c: objective.countryLocale,
    Language_Locale__c: objective.languageLocale,
    Dialect_Locale__c: objective.dialectLocale,
    Refresh__c: objective.refresh || false,
  };

  // Strongly sanitize numeric fields to avoid "Invalid decimal: false"
  const numericKeys = new Set([
    "Target_Contributors__c",
    "Unit_Pay_Rate__c",
    "Target_Production_Units__c",
    "Target_Production_Hours__c",
    "Unit_per_Hour__c",
    "Production_Payment_Threshold__c",
    "Quality_Payment_Threshold__c",
    "Minimum_Rate__c",
    "Maximum_Rate__c",
    "Project_Rate__c",
    "Project_Objective_Pay_Rate__c",
    "Flat_Pay_Rate__c",
    "Project_Incentive_Rate__c",
    "Days_Between_Reminder_Emails__c",
    "to_use_Funnel_A__c",
    "to_use_Funnel_B__c",
    "to_use_Funnel_C__c",
    "to_use_Funnel_D__c",
    "to_use_Funnel_E__c",
  ]);

  const cleaned = Object.keys(base).reduce((acc, key) => {
    const raw = base[key];
    // Drop null/undefined and empty strings universally
    if (raw === null || raw === undefined) return acc;
    if (typeof raw === "string" && raw.trim() === "") return acc;

    if (numericKeys.has(key)) {
      // Only include if a finite number
      const num = typeof raw === "number" ? raw : Number(raw);
      if (Number.isFinite(num)) {
        acc[key] = num;
      } else {
        // Skip invalid numeric like boolean false/true or non-numeric strings
        console.warn(`⚠️ Skipping non-numeric value for ${key}:`, raw);
      }
    } else {
      acc[key] = raw;
    }
    return acc;
  }, {});

  return cleaned;
};

// ==================== PROJECT APIs ====================

/**
 * Create a new Project in Salesforce
 *
 * Accepts both HTTP 200 (OK) and HTTP 201 (Created) as successful responses.
 * Salesforce may return 201 for newly created resources, which is the standard HTTP status code for creation.
 *
 * @param {Object} projectData - Complete project data from wizard
 * @returns {Promise} Salesforce response with created project
 */
export const createProject = async (projectData) => {
  let sfPayload;
  try {
    console.log("🚀 Creating project in Salesforce...");
    console.log("📥 Input projectData.metadata:", projectData.metadata);

    sfPayload = mapProjectMetadataToSalesforce(projectData.metadata);
    console.log(
      "📤 Salesforce payload being sent:",
      JSON.stringify(sfPayload, null, 2),
    );

    // TEMPORARY: User lookup fields are disabled for testing
    console.log(
      "⚠️ NOTE: All user lookup fields (Project Manager, Program Manager, etc.) are temporarily disabled for testing",
    );
    console.log(
      "⚠️ If project creation succeeds, the issue is with user IDs in the Salesforce org",
    );

    const response = await salesforceApi.post("/Project", sfPayload);
    console.log("✅ Response data (envelope):", response?.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating project:",
      error.response?.data || error.message,
    );
    console.error("❌ Failed payload:", sfPayload);
    throw error;
  }
};

/**
 * Get a specific Project by ID
 * @param {string} projectId - Salesforce Project ID
 * @returns {Promise} Project data
 */
export const getProject = async (projectId) => {
  try {
    const response = await salesforceApi.get(`/Project?id=${projectId}`);
    return response.data;
  } catch (error) {
    console.error(
      "Error getting project:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Get all Projects
 * @returns {Promise} Array of all projects
 */
export const getAllProjects = async () => {
  try {
    const response = await salesforceApi.get("/Project?id=all");
    return response.data;
  } catch (error) {
    console.error(
      "Error getting all projects:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Search Projects by keyword
 * @param {string} searchTerm - Search term
 * @returns {Promise} Array of matching projects
 */
export const searchProjects = async (searchTerm) => {
  try {
    const response = await salesforceApi.get(
      `/Project?searchkey=${searchTerm}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error searching projects:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Update an existing Project
 * @param {string} projectId - Salesforce Project ID
 * @param {Object} updates - Fields to update
 * @returns {Promise} Updated project data
 */
export const updateProject = async (projectId, updates) => {
  try {
    const response = await salesforceApi.patch(
      `/Project?id=${projectId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating project:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// ==================== PROJECT OBJECTIVE APIs ====================

/**
 * Create a new Project Objective in Salesforce
 *
 * Accepts both HTTP 200 (OK) and HTTP 201 (Created) as successful responses.
 * Salesforce may return 201 for newly created resources, which is the standard HTTP status code for creation.
 *
 * @param {Object} objectiveData - Objective data
 * @param {string} projectId - Parent Project Salesforce ID
 * @returns {Promise} Salesforce response with created objective
 */
export const createProjectObjective = async (objectiveData, projectId) => {
  let sfPayload;
  try {
    console.log("🎯 Creating objective in Salesforce...");
    console.log("📥 Objective data:", objectiveData);
    console.log("📥 Project ID:", projectId);

    sfPayload = mapObjectiveToSalesforce(objectiveData, projectId);
    // Defensive cleanup: ensure no null/empty values that Apex can't parse
    const cleanedPayload = Object.keys(sfPayload).reduce((acc, k) => {
      const v = sfPayload[k];
      if (
        v !== null &&
        v !== undefined &&
        !(typeof v === "string" && v.trim() === "")
      )
        acc[k] = v;
      return acc;
    }, {});
    console.log(
      "📤 Salesforce payload:",
      JSON.stringify(cleanedPayload, null, 2),
    );

    const response = await salesforceApi.post(
      "/ProjectObjective",
      cleanedPayload,
    );
    console.log("✅ Response data (envelope):", response?.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating objective:");
    console.error("- Error data:", error.response?.data || error.details);
    console.error("- Error message:", error.message);
    console.error("- Payload sent:", sfPayload);
    throw error;
  }
};

/**
 * Get all Objectives for a specific Project
 * @param {string} projectId - Salesforce Project ID
 * @returns {Promise} Array of objectives
 */
export const getProjectObjectives = async (projectId) => {
  try {
    const response = await salesforceApi.get(
      `/ProjectObjective?pid=${projectId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error getting objectives:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Get a specific Objective by ID
 * @param {string} objectiveId - Salesforce Objective ID
 * @returns {Promise} Objective data
 */
export const getObjective = async (objectiveId) => {
  try {
    const response = await salesforceApi.get(
      `/ProjectObjective?id=${objectiveId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error getting objective:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Get all Objectives
 * @returns {Promise} Array of all objectives
 */
export const getAllObjectives = async () => {
  try {
    const response = await salesforceApi.get("/ProjectObjective?searchkey=all");
    return response.data;
  } catch (error) {
    console.error(
      "Error getting all objectives:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Update an existing Project Objective
 * @param {string} objectiveId - Salesforce Objective ID
 * @param {Object} updates - Fields to update
 * @returns {Promise} Updated objective data
 */
export const updateProjectObjective = async (objectiveId, updates) => {
  try {
    const response = await salesforceApi.patch(
      `/ProjectObjective?id=${objectiveId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating objective:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// ==================== PROJECT QUALIFICATION STEP APIs ====================

/**
 * Map UI qualification step data to Salesforce fields
 */
const mapQualStepToSalesforce = (qualStep, projectId) => {
  // Use qualificationStepId if available (from searchable field), otherwise fallback to qualificationStep
  // But validate it's a valid ID (15 or 18 characters starting with 'a' or '0')
  const qualificationStepId =
    qualStep.qualificationStepId || qualStep.qualificationStep;
  const isValidId =
    qualificationStepId &&
    (qualificationStepId.length === 15 || qualificationStepId.length === 18) &&
    (qualificationStepId.startsWith("a") ||
      qualificationStepId.startsWith("0"));

  if (!isValidId && qualificationStepId) {
    console.warn(
      "⚠️ Invalid qualification step ID:",
      qualificationStepId,
      "- Expected Salesforce ID (15/18 chars starting with a/0)",
    );
  }

  // Validate Project Objective Id as well (must be a valid SF Id)
  const objectiveIdCandidate = qualStep.objectiveId || qualStep.objective;
  const isValidObjectiveId =
    objectiveIdCandidate &&
    (String(objectiveIdCandidate).length === 15 ||
      String(objectiveIdCandidate).length === 18) &&
    /^[a-zA-Z0-9]+$/.test(String(objectiveIdCandidate));

  return {
    Name: qualStep.name || qualStep.projectQualificationStepName,
    Project__c: projectId,
    Project_Objective__c: isValidObjectiveId
      ? String(objectiveIdCandidate)
      : null,
    Passing_Score__c: qualStep.passingScore
      ? Number(qualStep.passingScore)
      : null,
    Project_Qualification_Step_Status__c: qualStep.status || "Draft",
    Qualification_Step__c: isValidId ? qualificationStepId : null, // Only send if valid ID
    Funnel__c: qualStep.funnel || "A",
    Step_Number__c: qualStep.stepNumber ? Number(qualStep.stepNumber) : null,
    Number_of_Attempts__c: qualStep.numberOfAttempts
      ? Number(qualStep.numberOfAttempts)
      : 1,
    Estimated_Time_to_Complete_mins__c: qualStep.estimatedTime
      ? Number(qualStep.estimatedTime)
      : null,
  };
};

/**
 * Create a new Qualification Step in Salesforce
 *
 * Accepts both HTTP 200 (OK) and HTTP 201 (Created) as successful responses.
 *
 * @param {Object} qualStepData - Qualification step data
 * @param {string} projectId - Parent Project Salesforce ID
 * @returns {Promise} Salesforce response with created qualification step
 */
export const createQualificationStep = async (qualStepData, projectId) => {
  let sfPayload;
  try {
    console.log("📝 Creating qualification step in Salesforce...");
    console.log("📥 Qual Step data:", qualStepData);
    console.log("📥 Project ID:", projectId);

    sfPayload = mapQualStepToSalesforce(qualStepData, projectId);
    console.log("📤 Salesforce payload:", JSON.stringify(sfPayload, null, 2));

    const response = await salesforceApi.post("/ProjectQualStep", sfPayload);
    console.log("✅ Response data (envelope):", response?.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating qualification step:");
    console.error("- Error data:", error.response?.data);
    console.error("- Error message:", error.message);
    console.error("- Payload sent:", sfPayload);
    throw error;
  }
};

/**
 * Get all Qualification Steps for a specific Project
 */
export const getQualificationSteps = async (projectId) => {
  try {
    const response = await salesforceApi.get(
      `/ProjectQualStep?pid=${projectId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error getting qualification steps:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const updateQualificationStep = async (qualStepId, updates) => {
  try {
    const response = await salesforceApi.patch(
      `/ProjectQualStep?id=${qualStepId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating qualification step:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// ==================== PROJECT PAGE APIs ====================

/**
 * Map UI project page data to Salesforce fields
 */
const mapProjectPageToSalesforce = (pageData, projectId) => {
  const base = {
    Name: pageData.projectPageName || pageData.name,
    Project_Page_Type__c: pageData.projectPageType || pageData.type,
    Duplicate_Check__c: pageData.duplicateCheck || false,
    Active__c: pageData.active !== undefined ? pageData.active : true,
    Project_Qualification_Step__c: pageData.qualificationStep,
    Project_Page_Description__c:
      pageData.projectPageDescription || pageData.description,
    Project__c: projectId,
    // Include only when provided to avoid sending null
    ...(pageData.projectObjectiveId
      ? { Project_Objective__c: pageData.projectObjectiveId }
      : {}),
  };

  // Remove null/undefined/empty-string values; keep false and 0
  const cleaned = Object.keys(base).reduce((acc, key) => {
    const val = base[key];
    if (val === null || val === undefined) return acc;
    if (typeof val === "string" && val.trim() === "") return acc;
    acc[key] = val;
    return acc;
  }, {});

  return cleaned;
};

/**
 * Create a new Project Page in Salesforce
 *
 * Accepts both HTTP 200 (OK) and HTTP 201 (Created) as successful responses.
 *
 * @param {Object} pageData - Project page data
 * @param {string} projectId - Parent Project Salesforce ID
 * @returns {Promise} Salesforce response with created project page
 */
export const createProjectPage = async (pageData, projectId) => {
  let sfPayload;
  try {
    console.log("📄 Creating project page in Salesforce...");
    console.log("📥 Page data:", pageData);
    console.log("📥 Project ID:", projectId);

    sfPayload = mapProjectPageToSalesforce(pageData, projectId);
    console.log("📤 Salesforce payload:", JSON.stringify(sfPayload, null, 2));

    const response = await salesforceApi.post("/ProjectPage", sfPayload);
    console.log("✅ Response data (envelope):", response?.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating project page:");
    console.error("- Error data:", error.response?.data);
    console.error("- Error message:", error.message);
    console.error("- Payload sent:", sfPayload);
    throw error;
  }
};

/**
 * Get all Project Pages for a specific Project
 */
export const getProjectPages = async (projectId) => {
  try {
    const response = await salesforceApi.get(`/ProjectPage?pid=${projectId}`);
    return response.data;
  } catch (error) {
    console.error(
      "Error getting project pages:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const updateProjectPage = async (projectPageId, updates) => {
  try {
    const response = await salesforceApi.patch(
      `/ProjectPage?id=${projectPageId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating project page:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// ==================== PROJECT PAGE APIs ====================

/**
 * Map UI project page data to Salesforce fields
 */
const mapProjectPageComponentToSalesforce = (componentData, pageId) => {
  const base = {
    Project_Page__c: componentData.projectPage || pageId,
    Component_Order__c: componentData.componentOrder || "",
    Project_Page_Component_Desc__c: componentData.description || "",
    Headline_1__c: componentData.headline1 || "",
    Headline_2__c: componentData.headline2 || "",
    Headline_3__c: componentData.headline3 || "",
    Image_Location_1__c: componentData.imageLocation1 || "",
    Image_Location_2__c: componentData.imageLocation2 || "",
    Image_Location_3__c: componentData.imageLocation3 || "",
    Text_Area_1__c: componentData.textArea1 || "",
    Text_Area_2__c: componentData.textArea2 || "",
    Text_Area_3__c: componentData.textArea3 || "",
    Button_Type__c: componentData.buttonType || "",
    Button_Type_2__c: componentData.buttonType2 || "",
    Button_Type_3__c: componentData.buttonType3 || "",
    Button_Name_1__c: componentData.buttonName1 || "",
    Button_Name_2__c: componentData.buttonName2 || "",
    Button_Name_3__c: componentData.buttonName3 || "",
    Button_URL_1__c: componentData.buttonUrl1 || "",
    Button_URL_2__c: componentData.buttonUrl2 || "",
    Button_URL_3__c: componentData.buttonUrl3 || "",
    File_Download_URL_1__c: componentData.fileUrl1 || "",
    File_Download_URL_2__c: componentData.fileUrl2 || "",
    File_Download_URL_3__c: componentData.fileUrl3 || "",
  };

  // Remove null/undefined/empty-string values; keep false and 0
  const cleaned = Object.keys(base).reduce((acc, key) => {
    const val = base[key];
    if (val === null || val === undefined) return acc;
    if (typeof val === "string" && val.trim() === "") return acc;
    acc[key] = val;
    return acc;
  }, {});

  return cleaned;
};

/**
 * Create a new Project Page in Salesforce
 *
 * Accepts both HTTP 200 (OK) and HTTP 201 (Created) as successful responses.
 *
 * @param {Object} pageData - Project page data
 * @param {string} projectId - Parent Project Salesforce ID
 * @returns {Promise} Salesforce response with created project page
 */
export const createProjectPageComponent = async (componentData, pageId) => {
  let sfPayload;
  try {
    console.log("📄 Creating project page in Salesforce...");
    console.log("📥 Component data:", componentData);
    console.log("📥 Page ID:", pageId);

    sfPayload = mapProjectPageComponentToSalesforce(componentData, pageId);
    console.log("📤 Salesforce payload:", JSON.stringify(sfPayload, null, 2));

    const response = await salesforceApi.post(
      "/ProjectPageComponent",
      sfPayload,
    );
    console.log("✅ Response data (envelope):", response?.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating project page component:");
    console.error("- Error data:", error.response?.data);
    console.error("- Error message:", error.message);
    console.error("- Payload sent:", sfPayload);
    throw error;
  }
};

/**
 * Get all Project Pages for a specific Project
 */
export const getProjectPageComponents = async (projectId) => {
  try {
    const response = await salesforceApi.get(
      `/ProjectPageComponent?pid=${projectId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error getting project page components:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const updateProjectPageComponent = async (projectPageId, updates) => {
  try {
    const response = await salesforceApi.patch(
      `/ProjectPageComponent?id=${projectPageId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error updating project page component:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// ==================== BATCH OPERATIONS ====================

/**
 * Create a complete project with all objectives
 * This is the main function to call from the Wizard "Finish Setup" button
 *
 * @param {Object} projectData - Complete project data from wizard
 * @returns {Promise} Object with project ID and created objectives
 */
export const createCompleteProject = async (projectData) => {
  try {
    console.log("🚀 Starting complete project creation...", projectData);

    // Step 1: Create the Project
    const projectResponse = await createProject(projectData);
    console.log("📦 Raw project response:", projectResponse);
    console.log("📦 Response type:", typeof projectResponse);
    console.log(
      "📦 Response keys:",
      projectResponse ? Object.keys(projectResponse) : "null",
    );

    // Handle multiple response formats from Salesforce
    let projectData_sf;
    let createdProjectId;

    // Try to extract the project data and ID from various response formats
    if (
      projectResponse?.success &&
      projectResponse?.data &&
      Array.isArray(projectResponse.data) &&
      projectResponse.data.length > 0
    ) {
      // Format: { success: true, data: [{ Id: "...", ... }] }
      projectData_sf = projectResponse.data[0];
      createdProjectId = projectData_sf.Id;
      console.log("✅ Extracted from envelope format (success + data array)");
    } else if (
      projectResponse?.data &&
      Array.isArray(projectResponse.data) &&
      projectResponse.data.length > 0 &&
      projectResponse.data[0].Id
    ) {
      // Format: { data: [{ Id: "...", ... }] }
      projectData_sf = projectResponse.data[0];
      createdProjectId = projectData_sf.Id;
      console.log("✅ Extracted from data array format");
    } else if (projectResponse?.data?.Id) {
      // Format: { data: { Id: "...", ... } }
      projectData_sf = projectResponse.data;
      createdProjectId = projectResponse.data.Id;
      console.log("✅ Extracted from nested data object");
    } else if (projectResponse?.Id) {
      // Format: { Id: "...", ... } (direct Salesforce object)
      projectData_sf = projectResponse;
      createdProjectId = projectResponse.Id;
      console.log("✅ Extracted from direct object format");
    } else if (
      Array.isArray(projectResponse) &&
      projectResponse.length > 0 &&
      projectResponse[0].Id
    ) {
      // Format: [{ Id: "...", ... }] (array of objects)
      projectData_sf = projectResponse[0];
      createdProjectId = projectResponse[0].Id;
      console.log("✅ Extracted from array format");
    } else if (typeof projectResponse === "string") {
      // Some Apex endpoints may return just the Id as a plain string
      const idCandidate = String(projectResponse).trim();
      const isSfId = /^[a-zA-Z0-9]{15,18}$/.test(idCandidate);
      if (isSfId) {
        createdProjectId = idCandidate;
        projectData_sf = { Id: idCandidate };
        console.log("✅ Extracted from plain string Id format");
      }
    } else {
      // Unable to find ID in any known format
      console.error("❌ Could not extract project ID from response");
      console.error(
        "❌ Full response structure:",
        JSON.stringify(projectResponse, null, 2),
      );
      throw new Error(
        "Could not extract project ID from Salesforce response. Check console for details.",
      );
    }

    console.log("✅ Project created with ID:", createdProjectId);

    // Step 2: Create all Project Objectives
    const objectives = Array.isArray(projectData.objectives)
      ? projectData.objectives
      : [];
    const createdObjectives = [];

    for (const objective of objectives) {
      try {
        const objectiveResponse = await createProjectObjective(
          objective,
          createdProjectId,
        );
        let objData;

        if (Array.isArray(objectiveResponse) && objectiveResponse[0]?.Id) {
          objData = objectiveResponse[0];
        } else if (objectiveResponse?.Id) {
          objData = objectiveResponse;
        } else if (
          objectiveResponse?.data &&
          Array.isArray(objectiveResponse.data) &&
          objectiveResponse.data[0]?.Id
        ) {
          objData = objectiveResponse.data[0];
        } else if (objectiveResponse?.data?.Id) {
          objData = objectiveResponse.data;
        }

        if (objData) {
          createdObjectives.push(objData);
          console.log("✅ Objective created:", objData.Id);
        } else {
          console.warn(
            "⚠️ Unable to extract created Objective Id from response:",
            objectiveResponse,
          );
        }
      } catch (objError) {
        console.error(
          "❌ Failed to create objective:",
          objective.objectiveName,
          objError,
        );
        // Continue with other objectives even if one fails
      }
    }

    // Step 3: Create all Qualification Steps
    const qualificationSteps = Array.isArray(projectData.qualificationSteps)
      ? projectData.qualificationSteps
      : [];
    const createdQualSteps = [];

    // Build a lookup from objective name -> Id from created objectives to wire PQS references
    const objectiveNameToId = createdObjectives.reduce((acc, o) => {
      if (o && o.Id && o.Name) acc[String(o.Name).trim()] = o.Id;
      return acc;
    }, {});

    for (const qualStep of qualificationSteps) {
      try {
        // Always clear any stale objectiveId carried over from source
        // Then, if possible, map by objective name to the newly created Objective Id
        const objectiveName =
          qualStep.objective || qualStep.objectiveName || "";
        let effectiveQualStep = { ...qualStep };
        if ("objectiveId" in effectiveQualStep) {
          delete effectiveQualStep.objectiveId;
        }
        if (objectiveName) {
          const mappedId = objectiveNameToId[String(objectiveName).trim()];
          if (mappedId) {
            effectiveQualStep.objectiveId = mappedId;
          }
        }

        const qualStepResponse = await createQualificationStep(
          effectiveQualStep,
          createdProjectId,
        );
        let qualData;

        if (Array.isArray(qualStepResponse) && qualStepResponse[0]?.Id) {
          qualData = qualStepResponse[0];
        } else if (qualStepResponse?.Id) {
          qualData = qualStepResponse;
        } else if (
          qualStepResponse?.data &&
          Array.isArray(qualStepResponse.data) &&
          qualStepResponse.data[0]?.Id
        ) {
          qualData = qualStepResponse.data[0];
        } else if (qualStepResponse?.data?.Id) {
          qualData = qualStepResponse.data;
        }

        if (qualData) {
          createdQualSteps.push(qualData);
          console.log("✅ Qualification Step created:", qualData.Id);
        } else {
          console.warn(
            "⚠️ Unable to extract created Qualification Step Id from response:",
            qualStepResponse,
          );
        }
      } catch (qualError) {
        console.error(
          "❌ Failed to create qualification step:",
          qualStep.name,
          qualError,
        );
        // Continue with other steps even if one fails
      }
    }

    // Step 4: Create all Project Pages
    const pages = Array.isArray(projectData.pages) ? projectData.pages : [];
    const createdPages = [];

    for (const page of pages) {
      try {
        const pageResponse = await createProjectPage(page, createdProjectId);
        let pageData_sf;

        if (Array.isArray(pageResponse) && pageResponse[0]?.Id) {
          pageData_sf = pageResponse[0];
        } else if (pageResponse?.Id) {
          pageData_sf = pageResponse;
        } else if (
          pageResponse?.data &&
          Array.isArray(pageResponse.data) &&
          pageResponse.data[0]?.Id
        ) {
          pageData_sf = pageResponse.data[0];
        } else if (pageResponse?.data?.Id) {
          pageData_sf = pageResponse.data;
        }

        if (pageData_sf) {
          createdPages.push(pageData_sf);
          console.log("✅ Project Page created:", pageData_sf.Id);
        } else {
          console.warn(
            "⚠️ Unable to extract created Project Page Id from response:",
            pageResponse,
          );
        }
      } catch (pageError) {
        console.error(
          "❌ Failed to create project page:",
          page.projectPageName,
          pageError,
        );
        // Continue with other pages even if one fails
      }
    }
    // Step 5 : Create all Project Page Components
    const components = Array.isArray(projectData.components)
      ? projectData.components
      : [];
    const createdComponents = [];

    for (const component of components) {
      try {
        const componentResponse = await createProjectPageComponent(
          component,
          createdProjectId,
        );
        let componentData_sf;

        if (Array.isArray(componentResponse) && componentResponse[0]?.Id) {
          componentData_sf = componentResponse[0];
        } else if (componentResponse?.Id) {
          componentData_sf = componentResponse;
        } else if (
          componentResponse?.data &&
          Array.isArray(componentResponse.data) &&
          componentResponse.data[0]?.Id
        ) {
          componentData_sf = componentResponse.data[0];
        } else if (componentResponse?.data?.Id) {
          componentData_sf = componentResponse.data;
        }

        if (componentData_sf) {
          createdPages.push(componentData_sf);
          console.log("✅ Project Page created:", componentData_sf.Id);
        } else {
          console.warn(
            "⚠️ Unable to extract created Project Page Id from response:",
            componentResponse,
          );
        }
      } catch (pageError) {
        console.error(
          "❌ Failed to create project page Component:",
          component.projectPageName,
          pageError,
        );
        // Continue with other pages even if one fails
      }
    }

    console.log(`
✅ Successfully created complete project:
   - Project ID: ${createdProjectId}
   - Objectives: ${createdObjectives.length}
   - Qualification Steps: ${createdQualSteps.length}
   - Project Pages: ${createdPages.length}
   - Project Page Components : ${createdComponents.length}
    `);

    return {
      success: true,
      projectId: createdProjectId,
      project: projectData_sf,
      objectives: createdObjectives,
      qualificationSteps: createdQualSteps,
      pages: createdPages,
      components: createdComponents,
      message: `Project created successfully with ${createdObjectives.length} objective(s), ${createdQualSteps.length} qualification step(s), ${createdPages.length} page(s) and ${createdComponents.length} component(s)`,
    };
  } catch (error) {
    console.error("❌ Error in complete project creation:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};

// ============================================
// USER SEARCH OPERATIONS
// ============================================

/**
 * Search for Salesforce Users by email (partial match)
 * @param {string} email - Email to search for (can be partial)
 * @returns {Promise} - Array of matching users
 */
export const searchUsersByEmail = async (email) => {
  try {
    console.log("🔍 Searching users by email:", email);

    const response = await salesforceApi.get("/UserSearch", {
      params: { email },
    });

    console.log("✅ User search results:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error searching users:", error);
    throw error;
  }
};

/**
 * Get user by exact email match
 * @param {string} email - Exact email to search for
 * @returns {Promise} - User data with ID
 */
export const getUserByEmail = async (email) => {
  try {
    console.log("🔍 Getting user by exact email:", email);

    const response = await salesforceApi.post("/UserSearch", {
      email,
    });

    console.log("✅ User found:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error getting user:", error);
    throw error;
  }
};

// ==================== CLONE OPERATIONS ====================

/**
 * Clone a Project with optional nested objects
 * @param {string} projectId - Salesforce Project ID to clone
 * @param {Object} options - Clone options
 * @param {boolean} options.cloneObjectives - Clone project objectives
 * @param {boolean} options.cloneQualSteps - Clone qualification steps
 * @param {boolean} options.clonePages - Clone project pages
 * @returns {Promise} Cloned project data
 */
export const cloneProject = async (projectId, options = {}) => {
  try {
    console.log("Cloning project:", projectId, "with options:", options);

    const response = await salesforceApi.post(`/Project?id=${projectId}`, {
      cloneObjectives: options.cloneObjectives || false,
      cloneQualSteps: options.cloneQualSteps || false,
      clonePages: options.clonePages || false,
    });

    console.log("✅ Project cloned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error cloning project:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Clone a Project Objective with optional nested objects
 * @param {string} objectiveId - Salesforce Objective ID to clone
 * @param {Object} options - Clone options
 * @param {boolean} options.cloneQualSteps - Clone qualification steps
 * @param {boolean} options.clonePages - Clone project pages
 * @returns {Promise} Cloned objective data
 */
export const cloneProjectObjective = async (objectiveId, options = {}) => {
  try {
    console.log("Cloning objective:", objectiveId, "with options:", options);

    const response = await salesforceApi.post(
      `/ProjectObjective?id=${objectiveId}`,
      {
        cloneQualSteps: options.cloneQualSteps || false,
        clonePages: options.clonePages || false,
      },
    );

    console.log("✅ Objective cloned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error cloning objective:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Clone a Qualification Step
 * @param {string} qualStepId - Salesforce Qualification Step ID to clone
 * @returns {Promise} Cloned qualification step data
 */
export const cloneQualificationStep = async (qualStepId) => {
  try {
    console.log("Cloning qualification step:", qualStepId);

    const response = await salesforceApi.post(
      `/ProjectQualStep?id=${qualStepId}`,
      {},
    );

    console.log("✅ Qualification step cloned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error cloning qualification step:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Clone a Project Page with optional components
 * @param {string} pageId - Salesforce Project Page ID to clone
 * @param {Object} options - Clone options
 * @param {boolean} options.cloneComponents - Clone page components
 * @returns {Promise} Cloned page data
 */
export const cloneProjectPage = async (pageId, options = {}) => {
  try {
    console.log("Cloning project page:", pageId, "with options:", options);

    const response = await salesforceApi.post(`/ProjectPage?id=${pageId}`, {
      cloneComponents: options.cloneComponents || false,
    });

    console.log("✅ Project page cloned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error cloning project page:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Clone a Project Page Component
 * @param {string} componentId - Salesforce Page Component ID to clone
 * @returns {Promise} Cloned component data
 */
export const clonePageComponent = async (componentId) => {
  try {
    console.log("Cloning page component:", componentId);

    const response = await salesforceApi.post(
      `/ProjectPageComponent?id=${componentId}`,
      {},
    );

    console.log("✅ Page component cloned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error cloning page component:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export default {
  // Project operations
  createProject,
  getProject,
  getAllProjects,
  searchProjects,
  updateProject,

  // Objective operations
  createProjectObjective,
  getProjectObjectives,
  getObjective,
  getAllObjectives,
  updateProjectObjective,

  // Qualification Step operations
  createQualificationStep,
  getQualificationSteps,
  updateQualificationStep,

  // Project Page operations
  createProjectPage,
  getProjectPages,
  updateProjectPage,

  // Project Page Component Operations
  createProjectPageComponent,
  getProjectPageComponents,
  updateProjectPageComponent,

  // User search operations
  searchUsersByEmail,
  getUserByEmail,

  // Batch operations
  createCompleteProject,

  // Clone operations
  cloneProject,
  cloneProjectObjective,
  cloneQualificationStep,
  cloneProjectPage,
  clonePageComponent,
};
